import { Title, Stack, ActionIcon, Group, Button, Modal, TextInput } from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { unhandledApi } from "../api/unhandled.js";
import { notifications } from "@mantine/notifications";
import { useState } from "react";

interface PromoteForm {
  domain: string;
  clientDisplayName: string;
  folderId: string;
}

export function UnhandledSenders() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["unhandled"], queryFn: unhandledApi.list });
  const [promote, setPromote] = useState<PromoteForm | null>(null);

  const promoteMut = useMutation({
    mutationFn: (f: PromoteForm) => unhandledApi.promote(f.domain, f.clientDisplayName, f.folderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unhandled"] });
      qc.invalidateQueries({ queryKey: ["folders"] });
      notifications.show({ title: "Added as client", message: `Promoted ${promote?.domain}`, color: "green" });
      setPromote(null);
    },
  });

  const discard = useMutation({
    mutationFn: (domain: string) => unhandledApi.remove(domain),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["unhandled"] }),
  });

  return (
    <Stack>
      <Title order={2}>Unhandled senders ({data?.length ?? 0})</Title>
      <DataTable
        striped withTableBorder fetching={isLoading} idAccessor="domain"
        records={data ?? []}
        columns={[
          { accessor: "domain", title: "Domain" },
          { accessor: "count", title: "Hits", textAlign: "right", width: 80 },
          { accessor: "lastSeen", title: "Last seen", render: (r) => new Date(r.lastSeen).toLocaleString() },
          { accessor: "exampleSubject", title: "Example subject" },
          { accessor: "actions", title: "", width: 140, textAlign: "right",
            render: (r) => (
              <Group gap="xs" justify="flex-end">
                <ActionIcon variant="subtle" color="green" onClick={() =>
                  setPromote({ domain: r.domain, clientDisplayName: "", folderId: "" })}><IconCheck size={16} /></ActionIcon>
                <ActionIcon variant="subtle" color="red" onClick={() =>
                  { if (confirm(`Discard ${r.domain}?`)) discard.mutate(r.domain); }}><IconX size={16} /></ActionIcon>
              </Group>
            ),
          },
        ]}
      />

      <Modal opened={!!promote} onClose={() => setPromote(null)} title={`Add ${promote?.domain} as a client`}>
        <Stack>
          <TextInput label="Client display name" value={promote?.clientDisplayName ?? ""}
            onChange={(e) => promote && setPromote({ ...promote, clientDisplayName: e.currentTarget.value })} />
          <TextInput label="Outlook folder ID" value={promote?.folderId ?? ""}
            onChange={(e) => promote && setPromote({ ...promote, folderId: e.currentTarget.value })} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPromote(null)}>Cancel</Button>
            <Button onClick={() => promote && promoteMut.mutate(promote)} loading={promoteMut.isPending}>Add as client</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
